import { baseApi } from './baseApi';
import { buildListQueryParams } from '../utils/pagination';
import { runOptimisticCachedQueryPatches } from './queryCacheUtils';

const toComplaintFormData = (complaint = {}) => {
  const fd = new FormData();
  const { attachments, remove_attachment_ids, ...rest } = complaint;

  Object.entries(rest).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (value instanceof File) {
      fd.append(`complaint[${key}]`, value, value.name);
      return;
    }
    fd.append(`complaint[${key}]`, value);
  });

  if (Array.isArray(attachments)) {
    attachments.forEach((file) => {
      if (file instanceof File) {
        fd.append('complaint[attachments][]', file, file.name);
      }
    });
  }

  if (Array.isArray(remove_attachment_ids) && remove_attachment_ids.length > 0) {
    remove_attachment_ids.forEach((id) => {
      if (id == null || id === '') return;
      fd.append('complaint[remove_attachment_ids][]', String(id));
    });
  }

  return fd;
};

export const complaintApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getComplaintsDashboardCount: builder.query({
      query: () => '/api/complaints/dashboard_count',
    }),
    getCategoryWiseComplaints: builder.query({
      /** Pass one filter string, or [] / omit for all categories (no query param). */
      query: (filters = []) => {
        const list = (Array.isArray(filters) ? filters : [filters]).filter(Boolean);
        if (!list.length) return '/api/complaints/category_wise_complaints';
        const qs = list.map((f) => `filter=${encodeURIComponent(f)}`).join('&');
        return `/api/complaints/category_wise_complaints?${qs}`;
      },
    }),
    getComplaintsGraph: builder.query({
      /** Pass one period filter: this_month | last_month | last_3_months */
      query: (filters = []) => {
        const list = (Array.isArray(filters) ? filters : [filters]).filter(Boolean);
        if (!list.length) return '/api/complaints/complaints_graph';
        const qs = list.map((f) => `filter=${encodeURIComponent(f)}`).join('&');
        return `/api/complaints/complaints_graph?${qs}`;
      },
    }),
    getActiveComplaintLocations: builder.query({
      query: () => '/api/locations/active_location_list',
    }),
    getActiveNatureOfComplaintTypes: builder.query({
      query: ({ complaint_type } = {}) => {
        const type = String(complaint_type || '').toUpperCase();
        const safeType = type === 'CTM' || type === 'ITSM' ? type : '';
        return safeType
          ? `/api/nature_of_complaint_types/active_complaint_type_list?complaint_type=${encodeURIComponent(
              safeType
            )}`
          : '/api/nature_of_complaint_types/active_complaint_type_list';
      },
    }),
    getActiveCategories: builder.query({
      query: ({ category_complain_type, accountable_department_id } = {}) => {
        const params = new URLSearchParams();
        const type = String(category_complain_type || '').toUpperCase();
        const safeType = type === 'CTM' || type === 'ITSM' ? type : '';
        if (safeType) params.set('category_complain_type', safeType);
        if (
          accountable_department_id != null &&
          String(accountable_department_id).trim() !== ''
        ) {
          params.set(
            'accountable_department_id',
            String(accountable_department_id).trim()
          );
        }
        const qs = params.toString();
        return qs
          ? `/api/categories/active_category_list?${qs}`
          : '/api/categories/active_category_list';
      },
    }),
    getActiveSubcategoriesByCategory: builder.query({
      query: (categoryId) =>
        `/api/subcategories/active_subcategory_by_category?category_id=${categoryId}`,
    }),
    getWorkPriorities: builder.query({
      query: () => '/api/work_priorities',
    }),
    getIssueTypes: builder.query({
      query: (arg) => {
        const subcategoryId =
          arg && typeof arg === 'object'
            ? arg.subcategory_id ?? arg.subcategoryId
            : arg;
        if (subcategoryId != null && String(subcategoryId).trim() !== '') {
          return {
            url: '/api/issue_types',
            params: { subcategory_id: String(subcategoryId).trim() },
          };
        }
        return '/api/issue_types';
      },
    }),
    getAccountableDepartments: builder.query({
      query: ({ complaint_type } = {}) => {
        const type = String(complaint_type || '').toUpperCase();
        const safeType = type === 'CTM' || type === 'ITSM' ? type : '';
        return safeType
          ? `/api/accountable_departments?complaint_type=${encodeURIComponent(safeType)}`
          : '/api/accountable_departments';
      },
    }),
    getUserProfile: builder.query({
      query: (employeeId) => `/api/user_profile?employee_id=${employeeId}`,
    }),
    getComplaintRaisedByList: builder.query({
      query: (locationId) =>
        `/api/complaints/complaint_raised_by_list?location_id=${encodeURIComponent(
          String(locationId ?? '')
        )}`,
    }),
    getNatureOfComplaints: builder.query({
      query: (args = {}) => ({
        url: '/api/issue_types/index_nature_of_complaints',
        params: buildListQueryParams(args),
      }),
      providesTags: ['NatureOfComplaints'],
    }),
    getComplaints: builder.query({
      /** Pass filter, page, per_page, search, or other list query params. */
      query: (filters = {}) => ({
        url: '/api/complaints',
        params: buildListQueryParams(
          Array.isArray(filters)
            ? { filter: filters.filter(Boolean) }
            : typeof filters === 'string'
              ? { filter: filters }
              : filters
        ),
      }),
      providesTags: ['Complaints'],
    }),
    getComplaintById: builder.query({
      query: (complaintId) =>
        `/api/complaints/${encodeURIComponent(String(complaintId ?? ''))}`,
      providesTags: (_r, _e, complaintId) => [
        { type: 'Complaints', id: String(complaintId) },
      ],
    }),
    getRecentComplaints: builder.query({
      query: () => '/api/complaints/recent_complaints',
      providesTags: ['Complaints'],
    }),
    updateNatureOfComplaintStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/api/issue_types/${id}/update_status`,
        method: 'PATCH',
        body: { status },
      }),
      async onQueryStarted({ id, status }, { dispatch, getState, queryFulfilled }) {
        await runOptimisticCachedQueryPatches({
          dispatch,
          getState,
          queryFulfilled,
          endpointName: 'getNatureOfComplaints',
          updateDraft: (draft) => {
            const list = draft?.data;
            if (!Array.isArray(list)) return;
            const row = list.find(
              (r) =>
                Number(r.id ?? r.issue_type_id) === Number(id)
            );
            if (row) row.status = status;
          },
        });
      },
    }),
    createComplaint: builder.mutation({
      query: (complaint) => ({
        url: '/api/complaints',
        method: 'POST',
        body: toComplaintFormData(complaint),
      }),
      invalidatesTags: ['Complaints'],
    }),
    updateComplaint: builder.mutation({
      query: ({ id, complaint }) => ({
        url: `/api/complaints/${id}`,
        method: 'PUT',
        body: toComplaintFormData(complaint),
      }),
      invalidatesTags: ['Complaints'],
    }),
    createNatureOfComplaint: builder.mutation({
      query: ({
        name,
        accountable_department_id,
        category_id,
        subcategory_id,
        status = true,
      }) => ({
        url: '/api/issue_types',
        method: 'POST',
        body: {
          issue_type: {
            name,
            accountable_department_id,
            category_id,
            subcategory_id,
            status,
          },
        },
      }),
      invalidatesTags: ['NatureOfComplaints'],
    }),
    updateNatureOfComplaint: builder.mutation({
      query: ({
        id,
        name,
        accountable_department_id,
        category_id,
        subcategory_id,
      }) => ({
        url: `/api/issue_types/${id}`,
        method: 'PUT',
        body: {
          name,
          accountable_department_id,
          category_id,
          subcategory_id,
        },
      }),
      invalidatesTags: ['NatureOfComplaints'],
    }),

    getIssueTypeById: builder.query({
      query: (id) => `/api/issue_types/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'NatureOfComplaints', id }],
    }),

    createComplaintComment: builder.mutation({
      query: ({
        comment,
        account_id,
        commented_by_type,
        complaint_id,
        status,
        image,
        rca,
        pa,
        ca,
        accountable_department_id,
      }) => {
        const fd = new FormData();
        fd.append('comment', comment ?? '');
        fd.append('account_id', String(account_id ?? '').trim());
        fd.append('commented_by_type', String(commented_by_type ?? '').trim());
        fd.append('complaint_id', String(complaint_id ?? '').trim());
        if (status != null && String(status).trim() !== '') {
          fd.append('status', String(status).trim());
        }
        if (image instanceof File) fd.append('image', image, image.name);
        if (rca != null && String(rca).trim() !== '') fd.append('rca', String(rca).trim());
        if (pa != null && String(pa).trim() !== '') fd.append('pa', String(pa).trim());
        if (ca != null && String(ca).trim() !== '') fd.append('ca', String(ca).trim());
        if (
          accountable_department_id != null &&
          String(accountable_department_id).trim() !== ''
        ) {
          fd.append(
            'accountable_department_id',
            String(accountable_department_id).trim()
          );
        }
        return {
          url: '/api/complaint_comments',
          method: 'POST',
          body: fd,
        };
      },
      invalidatesTags: ['Complaints'],
    }),
    getComplaintTrackingStatus: builder.query({
      query: (complaintId) =>
        `/api/complaint_comments/tracking_status?complaint_id=${encodeURIComponent(
          String(complaintId ?? '').trim()
        )}`,
    }),
    createComplaintRating: builder.mutation({
      query: ({ complaint_id, rating, feedback }) => {
        const fd = new FormData();
        fd.append('complaint_id', String(complaint_id ?? '').trim());
        fd.append('rating', String(rating ?? '').trim());
        fd.append('feedback', String(feedback ?? '').trim());
        return {
          url: '/api/complaint_ratings',
          method: 'POST',
          body: fd,
        };
      },
      invalidatesTags: ['Complaints'],
    }),
  }),
});

export const {
  useGetComplaintsDashboardCountQuery,
  useGetCategoryWiseComplaintsQuery,
  useGetComplaintsGraphQuery,
  useGetActiveComplaintLocationsQuery,
  useGetActiveNatureOfComplaintTypesQuery,
  useGetActiveCategoriesQuery,
  useGetActiveSubcategoriesByCategoryQuery,
  useGetWorkPrioritiesQuery,
  useGetIssueTypesQuery,
  useGetIssueTypeByIdQuery,
  useGetAccountableDepartmentsQuery,
  useGetUserProfileQuery,
  useGetComplaintRaisedByListQuery,
  useCreateComplaintMutation,
  useUpdateComplaintMutation,
  useCreateNatureOfComplaintMutation,
  useGetNatureOfComplaintsQuery,
  useGetComplaintsQuery,
  useGetComplaintByIdQuery,
  useGetRecentComplaintsQuery,
  useUpdateNatureOfComplaintStatusMutation,
  useUpdateNatureOfComplaintMutation,
  useCreateComplaintCommentMutation,
  useCreateComplaintRatingMutation,
  useGetComplaintTrackingStatusQuery,
  useLazyGetComplaintTrackingStatusQuery,
} = complaintApi;

